import { toJS } from 'mobx';

import { ContextStore } from 'bernie-core';

import {
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsAdvancedFilterType,
  FlightsTripType,
  FlightsCabinClass,
  FlightsTravelerType,
  DateInput,
  FlightsDetailCriteriaInput,
  FlightsSearchFilterValuesInput,
  FlightsSortOptionTypeInput
} from '__generated__/api';
import { formatDateInputIntoString } from '.';

interface LegType {
  departureLocation: string;
  arrivalLocation: string;
  departureDate: DateInput;
}

interface SearchParametersType {
  airlinePreferenceCode: string;
  flightAirCabinClass: string;
  flightRouteType: string;
  journeysContinuationId?: string;
  legSearchInfoList: LegType[];
  nonstopOnly: number;
  refundableFlightsOnly: string;
  travelerCategoryInfo: {
    adultCount: number;
    childAges: [number];
    childCount: number;
    infantSeatingOnLap: boolean;
    totalPassengers: number;
  };
  searchFilterValuesList: FlightsSearchFilterValuesInput[];
  sortOption: FlightsSortOptionTypeInput;
}

const TripType = {
  [FlightsTripType.MULTI_DESTINATION]: 'multi',
  [FlightsTripType.ONE_WAY]: 'oneway',
  [FlightsTripType.ROUND_TRIP]: 'roundtrip'
};

const CabinClass = {
  [FlightsCabinClass.BUSINESS]: 'business',
  [FlightsCabinClass.COACH]: 'coach',
  [FlightsCabinClass.FIRST]: 'first',
  [FlightsCabinClass.PREMIUM_ECONOMY]: 'premium_economy'
};

interface DeepLinkType {
  leg1?: string;
  leg2?: string;
  leg3?: string;
  leg4?: string;
  leg5?: string;
  mode: string;
  options?: string;
  passengers?: string;
  trip?: string;
  filters?: string;
  sortOrder?: string;
  sortType?: string;
  journeysContinuationId?: string;
  pageId?: string;
}

interface DeepLinkInformationType {
  journeyContinuationId?: string;
}

const getPreferredAirline = ({ airlinePreferenceCode }: SearchParametersType) =>
  airlinePreferenceCode === '' ? '' : airlinePreferenceCode;

const getPreferredClass = ({ flightAirCabinClass }: SearchParametersType) =>
  flightAirCabinClass === FlightsCabinClass.COACH ? '' : CabinClass[flightAirCabinClass];

const getStopPreference = ({ nonstopOnly }: SearchParametersType) => nonstopOnly;

const getPenaltyPreference = ({ refundableFlightsOnly }: SearchParametersType) => refundableFlightsOnly;

const getAdultCount = ({ travelerCategoryInfo }: SearchParametersType) => travelerCategoryInfo.adultCount;

const getInfantInfo = ({ travelerCategoryInfo }: SearchParametersType) =>
  travelerCategoryInfo.infantSeatingOnLap ? 'Y' : 'N';

const getChildInfo = ({ travelerCategoryInfo }: SearchParametersType) => {
  const { childCount } = travelerCategoryInfo;
  const childAges = travelerCategoryInfo.childAges
    .filter(age => {
      return age !== -1;
    })
    .join(';');

  return childCount > 0 ? `${childCount}[${childAges}]` : childCount;
};

const paramToFunctionMap = {
  options: {
    'carrier:': getPreferredAirline,
    'cabinclass:': getPreferredClass,
    'maxhops:': getStopPreference,
    'nopenalty:': getPenaltyPreference
  },
  passengers: {
    'adults:': getAdultCount,
    'children:': getChildInfo,
    'infantinlap:': getInfantInfo
  }
};

const generateOptionsArray = (deepLinkObject: DeepLinkType, searchParameters: SearchParametersType, type) => {
  deepLinkObject[type] = Object.keys(paramToFunctionMap[type])
    .map(option => `${option}${paramToFunctionMap[type][option](searchParameters)}`)
    .join(',');
};

const addOptions = (deepLinkObject: DeepLinkType, searchParameters: SearchParametersType) => {
  generateOptionsArray(deepLinkObject, searchParameters, 'options');
};

const addPassengers = (deepLinkObject: DeepLinkType, searchParameters: SearchParametersType) => {
  generateOptionsArray(deepLinkObject, searchParameters, 'passengers');
};

const addLegs = (deepLinkObject: DeepLinkType, { legSearchInfoList }: SearchParametersType, context: ContextStore) => {
  legSearchInfoList.forEach((leg, index) => {
    const legStringValue = `from:${leg.departureLocation},to:${
      leg.arrivalLocation
    },departure:${formatDateInputIntoString(leg.departureDate, context)}TANYT`;

    deepLinkObject[`leg${index + 1}`] = legStringValue;
  });
};

const addFilters = (deepLinkObject: DeepLinkType, { searchFilterValuesList }: SearchParametersType) => {
  const filters = toJS(searchFilterValuesList);

  if (filters.length > 0) {
    deepLinkObject.filters = JSON.stringify(filters);
  }
};

const addSort = (deepLinkObject: DeepLinkType, { sortOption }: SearchParametersType) => {
  const sort = toJS(sortOption);

  if (sort && sort.sortOrder && sort.sortType) {
    deepLinkObject.sortOrder = sort.sortOrder;
    deepLinkObject.sortType = sort.sortType;
  }
};

const addTripType = (deepLinkObject: DeepLinkType, { flightRouteType }: SearchParametersType) =>
  (deepLinkObject.trip = TripType[flightRouteType]);

const addJCID = (deepLinkObject: DeepLinkType, { journeysContinuationId }: SearchParametersType) => {
  if (journeysContinuationId) {
    deepLinkObject.journeysContinuationId = journeysContinuationId;
  }
};

export const generateDeeplink = (searchParameters: SearchParametersType, context: ContextStore, pageId?: string) => {
  const deepLink: DeepLinkType = {
    mode: 'search'
  };

  addJCID(deepLink, searchParameters);
  addTripType(deepLink, searchParameters);
  addLegs(deepLink, searchParameters, context);
  addPassengers(deepLink, searchParameters);
  addOptions(deepLink, searchParameters);
  addFilters(deepLink, searchParameters);
  addSort(deepLink, searchParameters);

  if (pageId) {
    deepLink.pageId = pageId;
  }

  return deepLink;
};

export const generateInformationDeeplink = (flightsDetailCriteria: FlightsDetailCriteriaInput) => {
  const deepLink: DeepLinkInformationType = {
    journeyContinuationId: flightsDetailCriteria && flightsDetailCriteria.journeyContinuationId
  };

  return deepLink;
};

export const createSearchParameters = ({
  flightsSearchContext,
  travelerDetails,
  journeyCriteria,
  searchPreferences,
  searchFilterValuesList,
  sortOption
}: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables) => {
  const flightRouteType = flightsSearchContext.tripType || '';
  const journeysContinuationId = flightsSearchContext.journeysContinuationId || null;
  const legSearchInfoList = journeyCriteria.map(leg => ({
    departureLocation: leg.origin,
    arrivalLocation: leg.destination,
    departureDate: toJS(leg.departureDate)
  }));
  let travelerCategoryInfo;
  if (travelerDetails.length > 0) {
    const child = travelerDetails.find(e => e.travelerType === FlightsTravelerType.CHILD);
    const adult = travelerDetails.find(e => e.travelerType === FlightsTravelerType.ADULT);
    const infantsInSeat = travelerDetails.find(e => e.travelerType === FlightsTravelerType.INFANT_IN_SEAT);
    const infantsInLap = travelerDetails.find(e => e.travelerType === FlightsTravelerType.INFANT_IN_LAP);
    const adultCount = adult ? adult.count : 0;
    const totalPassengers = travelerDetails.reduce((a, { count }) => count + a, 0);
    const infantSeatingOnLap = infantsInLap !== undefined;
    let childAges = child ? toJS(child.ages) : [];
    let childCount = child ? child.count : 0;

    if (infantsInSeat) {
      childCount += infantsInSeat.count;
      childAges = childAges.concat(toJS(infantsInSeat.ages));
    }

    if (infantsInLap) {
      childCount += infantsInLap.count;
      childAges = childAges.concat(toJS(infantsInLap.ages));
    }

    travelerCategoryInfo = {
      adultCount,
      childAges,
      childCount,
      infantSeatingOnLap,
      totalPassengers
    };
  }

  const airlinePreferenceCode = searchPreferences.airline || '';
  const flightAirCabinClass = searchPreferences.cabinClass || '';
  const refundableFlightsOnly =
    searchPreferences.advancedFilters &&
    searchPreferences.advancedFilters.find(e => e === FlightsAdvancedFilterType.REFUNDABLE)
      ? 'Y'
      : 'N';
  const nonstopOnly =
    searchPreferences.advancedFilters &&
    searchPreferences.advancedFilters.find(e => e === FlightsAdvancedFilterType.NONSTOP)
      ? 0
      : 1;

  return {
    flightRouteType,
    legSearchInfoList,
    travelerCategoryInfo,
    airlinePreferenceCode,
    flightAirCabinClass,
    refundableFlightsOnly,
    nonstopOnly,
    searchFilterValuesList,
    sortOption,
    journeysContinuationId
  };
};

export const generateSearchQuery = (deepLinkObject: DeepLinkType): string => {
  const formatQueryParam = (param: string) => `${param}=${deepLinkObject[param]}`;

  return Object.keys(deepLinkObject).map(formatQueryParam).join('&');
};
